import React, { useEffect, useState } from "react";
import { consoleApi } from "../../../services/consoleApiService";

const DATE_RANGES = [
  { value: "", label: "Any time" },
  { value: "this-week", label: "This week" },
  { value: "this-weekend", label: "This weekend" },
  { value: "next-7-days", label: "Next 7 days" },
  { value: "next-30-days", label: "Next 30 days" },
];

const PILLARS = [
  { value: "problem-reveal", label: "Problem → Reveal" },
  { value: "feature-spotlight", label: "Feature Spotlight" },
  { value: "club-spotlight", label: "Club Spotlight" },
  { value: "comparison", label: "Comparison" },
  { value: "how-to", label: "How To" },
];

const PLATFORMS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
];

const PERSONAS = [
  { value: "", label: "— Not specified —" },
  { value: "solo-runner", label: "Solo Runner" },
  { value: "club-organizer", label: "Club Organizer" },
  { value: "returning-runner", label: "Returning Runner" },
];

const MOODS = [
  { value: "", label: "Auto (by pillar)" },
  { value: "night-tempo", label: "Night Tempo · slate", swatch: "#787f9a" },
  { value: "steady-slate", label: "Steady Slate · deep", swatch: "#3d4256" },
  { value: "verified-forest", label: "Verified Forest · green", swatch: "#3a6a4c" },
  { value: "warning-dusk", label: "Warning Dusk · amber", swatch: "#f59e0b" },
];

export default function BriefForm({ initial, onClose, onSubmit }) {
  const [topic, setTopic] = useState(initial?.topic || "");
  const [pillar, setPillar] = useState(initial?.pillar || "problem-reveal");
  const [platform, setPlatform] = useState(initial?.platform || "instagram");
  const [mood, setMood] = useState(initial?.mood || "");
  const [slideCount, setSlideCount] = useState(initial?.slide_count || 5);
  const [persona, setPersona] = useState(initial?.target_persona || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // Live data targeting
  const [showTarget, setShowTarget] = useState(
    Boolean(initial?.target_city_id || initial?.target_club_id || initial?.target_meeting_id || initial?.target_date_range)
  );
  const [cities, setCities] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [targetCityId, setTargetCityId] = useState(initial?.target_city_id || "");
  const [targetClubId, setTargetClubId] = useState(initial?.target_club_id || "");
  const [targetMeetingId, setTargetMeetingId] = useState(initial?.target_meeting_id || "");
  const [targetDateRange, setTargetDateRange] = useState(initial?.target_date_range || "");
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadingMeetings, setLoadingMeetings] = useState(false);

  // Load cities when the targeting section is first opened
  useEffect(() => {
    if (!showTarget || cities.length) return;
    consoleApi.cities
      .list()
      .then((data) => setCities(Array.isArray(data) ? data : data?.cities || []))
      .catch(() => {});
  }, [showTarget, cities.length]);

  // Cascade: city → clubs
  useEffect(() => {
    if (!targetCityId) {
      setClubs([]);
      return;
    }
    setLoadingClubs(true);
    consoleApi.clubs
      .list({ city_id: targetCityId, limit: 200 })
      .then((data) => setClubs(Array.isArray(data) ? data : data?.clubs || []))
      .catch(() => setClubs([]))
      .finally(() => setLoadingClubs(false));
  }, [targetCityId]);

  // Cascade: club or city → upcoming meetings
  useEffect(() => {
    if (!targetClubId && !targetCityId) {
      setMeetings([]);
      return;
    }
    setLoadingMeetings(true);
    const params = {};
    if (targetClubId) params.club_id = targetClubId;
    else if (targetCityId) params.city_id = targetCityId;
    consoleApi.content
      .upcomingMeetings(params)
      .then((data) => setMeetings(Array.isArray(data) ? data : []))
      .catch(() => setMeetings([]))
      .finally(() => setLoadingMeetings(false));
  }, [targetClubId, targetCityId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      setErr("Topic is required");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await onSubmit({
        topic: topic.trim(),
        pillar,
        platform,
        mood: mood || undefined,
        slide_count: Number(slideCount) || 5,
        target_persona: persona || undefined,
        notes: notes.trim() || undefined,
        target_city_id: targetCityId || undefined,
        target_club_id: targetClubId || undefined,
        target_meeting_id: targetMeetingId || undefined,
        target_date_range: targetDateRange || undefined,
      });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Failed to create brief");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="content-hub__modal-backdrop" onClick={onClose}>
      <div
        className="content-hub__modal"
        role="dialog"
        aria-label="New brief"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="content-hub__modal-header">
          <h2>New brief</h2>
          <button type="button" className="content-hub__icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form className="content-hub__form" onSubmit={handleSubmit}>
          <label className="content-hub__field">
            <span>Topic</span>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder='e.g. "Feature spotlight: the Runcard"'
              maxLength={500}
              autoFocus
              required
            />
          </label>
          <div className="content-hub__row">
            <label className="content-hub__field">
              <span>Pillar</span>
              <select value={pillar} onChange={(e) => setPillar(e.target.value)}>
                {PILLARS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
            <label className="content-hub__field">
              <span>Platform</span>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="content-hub__row">
            <label className="content-hub__field">
              <span>Slides</span>
              <input
                type="number"
                min={1}
                max={10}
                value={slideCount}
                onChange={(e) => setSlideCount(e.target.value)}
              />
            </label>
            <label className="content-hub__field">
              <span>Target persona</span>
              <select value={persona} onChange={(e) => setPersona(e.target.value)}>
                {PERSONAS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="content-hub__field">
            <span>Visual mood</span>
            <div className="content-hub__mood-row">
              <select value={mood} onChange={(e) => setMood(e.target.value)}>
                {MOODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              {mood && (
                <span
                  className="content-hub__mood-swatch"
                  style={{ background: MOODS.find((m) => m.value === mood)?.swatch }}
                  aria-hidden="true"
                />
              )}
            </div>
          </label>
          <label className="content-hub__field">
            <span>Notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={5000}
              rows={3}
              placeholder="Anything Claude should keep in mind"
            />
          </label>

          {/* Live data targeting */}
          <div className="content-hub__target">
            <button
              type="button"
              className="content-hub__target-toggle"
              onClick={() => setShowTarget((v) => !v)}
              aria-expanded={showTarget}
            >
              <span>{showTarget ? "▾" : "▸"} Live data target</span>
              <span className="content-hub__target-hint">
                Pull real clubs &amp; runs into the copy
              </span>
            </button>
            {showTarget && (
              <div className="content-hub__target-body">
                <div className="content-hub__row">
                  <label className="content-hub__field">
                    <span>City</span>
                    <select
                      value={targetCityId}
                      onChange={(e) => {
                        setTargetCityId(e.target.value);
                        setTargetClubId("");
                        setTargetMeetingId("");
                      }}
                    >
                      <option value="">— Any city —</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="content-hub__field">
                    <span>Club{loadingClubs ? " (loading…)" : ""}</span>
                    <select
                      value={targetClubId}
                      onChange={(e) => {
                        setTargetClubId(e.target.value);
                        setTargetMeetingId("");
                      }}
                      disabled={!targetCityId}
                    >
                      <option value="">{targetCityId ? "— Any club —" : "Pick a city first"}</option>
                      {clubs.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="content-hub__row">
                  <label className="content-hub__field">
                    <span>Specific run{loadingMeetings ? " (loading…)" : ""}</span>
                    <select
                      value={targetMeetingId}
                      onChange={(e) => setTargetMeetingId(e.target.value)}
                      disabled={!targetClubId && !targetCityId}
                    >
                      <option value="">
                        {targetClubId || targetCityId ? "— Any run —" : "Pick a city/club first"}
                      </option>
                      {meetings.map((m) => (
                        <option key={m.id} value={m.id}>
                          {[m.club, m.name, m.when].filter(Boolean).join(" · ")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="content-hub__field">
                    <span>Date window</span>
                    <select
                      value={targetDateRange}
                      onChange={(e) => setTargetDateRange(e.target.value)}
                    >
                      {DATE_RANGES.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="content-hub__target-note">
                  Live data uses public Club Card info — verify dates before posting.
                </p>
              </div>
            )}
          </div>

          {err && <div className="content-hub__error">{err}</div>}
          <div className="content-hub__modal-footer">
            <button type="button" className="content-hub__btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="content-hub__btn content-hub__btn--primary"
              disabled={submitting}
            >
              {submitting ? "Creating…" : "Create brief"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
